import { green, orange, red } from "@mui/material/colors";
import i18nProvider from "i18n/i18nProvider";
import _get from "lodash/get";
import _uniq from "lodash/uniq";
import { InvoiceRecordType } from "pages/Booking/Booking.types";
import { CategoryRecordType } from "pages/Category/Category.types";
import { CustomFieldRecordType } from "pages/CustomField/CustomField.types";
import { NameEntityRecordType } from "pages/NameEntity/NameEntity.types";
import { Children, Fragment, isValidElement, ReactNode } from "react";
import { Identifier, RaRecord, TranslateFunction } from "react-admin";

import { SettingType } from "../App.types";
import { EventDateRecordType, EventRecordType } from "../pages/Event/Event.types";
import {
  AttendeeRecordType,
  ContactInfoType,
  PersonRecordType,
  PriceRecordType,
  TaxRateRecordType,
  UserRecordType,
} from "../pages/SharedRecords.types";
import {
  datetimeDistance,
  datetimeSpanGermanFormat,
  dayGermanFormat,
  formatAmount,
  nonBreakingSpace,
  StringOrDateType,
} from "./formatHelpers";

function organizationNames(record: Partial<UserRecordType | AttendeeRecordType>) {
  const { main_contact_info, invoice_contact_info, invoice_dispatch_contact_info } = record;
  const contactInfos = [main_contact_info, invoice_contact_info, invoice_dispatch_contact_info];
  const contactOrgNames = contactInfos
    .filter((c) => c && c.organization_name)
    .map((c) => c && c.organization_name) as string[];

  const memberships = (record as Partial<AttendeeRecordType>).meta?.organization_memberships;
  const membershipNames = memberships?.map((m) => m.name) ?? [];

  return [...contactOrgNames, ...membershipNames];
}

export function renderFirstOrganizationName(record?: UserRecordType | AttendeeRecordType) {
  if (!record) return "";
  const names = organizationNames(record);
  return names[0] ?? null;
}

export function renderOrganizationNames(record?: Partial<UserRecordType>) {
  if (!record) return "";
  const uniqueOrganizationNames = _uniq(organizationNames(record));
  return `${uniqueOrganizationNames.join(", ")}`;
}

export function renderNameWithOrganization(record?: UserRecordType | AttendeeRecordType) {
  if (!record) return "";
  let name = renderFullName(record, { skipTitle: true });
  const organizationName = renderFirstOrganizationName(record);
  if (organizationName) {
    name += ` (${organizationName})`;
  }

  return name;
}

export function renderUserMetadata(translate: TranslateFunction) {
  return (record?: PersonRecordType | UserRecordType | AttendeeRecordType) => {
    if (!record) return "";
    const { birthday } = record;
    return birthday && translate("born", { date: dayGermanFormat(birthday) });
  };
}

export function renderEmails(record?: UserRecordType) {
  if (!record) {
    return "";
  }

  const { login_email, main_contact_info, invoice_contact_info, invoice_dispatch_contact_info } = record;
  const allContactOptions = [main_contact_info, invoice_contact_info, invoice_dispatch_contact_info];
  const contactInfoEmails = allContactOptions.filter((o) => !!o && o.email).map((o) => o && o.email);

  if (login_email) {
    contactInfoEmails.push(login_email);
  }

  return contactInfoEmails.join(", ");
}

export function renderNameWithId(record: NameEntityRecordType) {
  if (!record) return "";
  return `${record.name} (#${record.id})`;
}

export const renderEmailReceiver = (email: string) => (record: { first_name: string; last_name: string }) =>
  `<${record.first_name} ${record.last_name} ${email}>`;

export function renderEventName(record: EventRecordType) {
  const { meta } = record;
  if (!meta) {
    return "";
  }
  let name = meta.event_base_name;
  if (meta.group_parent_name) {
    name = `${meta.event_base_name} (${meta.group_parent_name})`;
  }

  return name;
}

export function renderEventNameWithIdNumber(record: EventRecordType) {
  const { id_number } = record;
  let name = renderEventName(record);
  if (!name) return "";
  if (id_number) name = `${id_number} / ${name}`;
  return name;
}

function appendDateToText(text?: string, firstDay?: StringOrDateType | null) {
  if (!text) return "";

  if (firstDay) {
    text += ` / ${dayGermanFormat(firstDay)}`;
  }

  return text;
}

export function renderEventDateWithTime(translate: TranslateFunction, eventDate: EventDateRecordType): string {
  const date = dayGermanFormat(eventDate.day);
  const time = renderTimespan(translate, eventDate);
  return time ? `${date} ${time}` : date;
}

export function renderEventNameWithDateAndIdNumber(record?: EventRecordType) {
  if (!record) return "";
  const { first_day } = record;
  const text = renderEventNameWithIdNumber(record);
  return appendDateToText(text, first_day);
}

export function renderEventNameWithDate(record?: EventRecordType) {
  if (!record) return "";
  const { first_day } = record;
  const text = renderEventName(record);
  return appendDateToText(text, first_day);
}

export function renderEventDateWithFallback(translate: TranslateFunction, attr: keyof EventRecordType = "first_day") {
  return (record: EventRecordType) => {
    if (!record) return "";
    if (record[attr]) return dayGermanFormat(record[attr] as StringOrDateType);
    return translate("no_date");
  };
}

export function renderLocationName(record: EventRecordType) {
  return record.meta.event_location_name
    ? record.meta.event_location_rooms.length
      ? `${record.meta.event_location_name} - ${record.meta.event_location_rooms.join(", ")}`
      : record.meta.event_location_name
    : "";
}

export function renderCurrency(attr = "amount", formatOpts = {}) {
  return (record?: Partial<RaRecord>) => {
    if (!record) return "";
    const amount = _get(record, attr) as number;
    if (!amount && amount !== 0) return null;

    return flatRenderCurrency(amount, formatOpts);
  };
}

export function flatRenderCurrency(amount: number, formatOpts: Intl.NumberFormatOptions) {
  return `${formatAmount(amount, formatOpts)}${nonBreakingSpace}€`;
}

export function renderPriceWithAmount(record?: PriceRecordType) {
  if (!record) return "";
  return `${record.price_type_name} (${record.amount.toFixed(2)}${nonBreakingSpace}€)`;
}

export function renderSettingsKey(record: SettingType, translate: TranslateFunction) {
  if (!record || !record.key) return "";
  return translate(`settings.${record.key}.name`);
}

export function renderSettingsValue(record: SettingType, translate: TranslateFunction) {
  if (!record) return "";

  if (typeof record.value == "boolean") {
    return renderBooleanValue(record.value, translate);
  }
  return record.value;
}

export function renderRoomIdentifierWithLocationName(record?: {
  identifier: string;
  meta?: { location_name: string };
}) {
  if (!record) return "";
  if (!record.meta) return record.identifier;
  return `${record.identifier} (${record.meta.location_name})`;
}

export function renderTimespan(
  translate: TranslateFunction,
  record?: { start_time?: string | null; end_time?: string | null },
) {
  if (!record) return "";

  const { start_time, end_time } = record;

  if (!(start_time && end_time)) return null;
  const end = translate("oclock", { time: end_time });

  return `${start_time} - ${end}`;
}

export function renderDatetimeSpan(record: { start_datetime?: string | null; end_datetime?: string | null }) {
  if (!record.start_datetime || !record.end_datetime) return;
  return datetimeSpanGermanFormat(record.start_datetime, record.end_datetime);
}

export function renderDatetimeDistanceFromNow(attr: string) {
  return (record: object = {}) => {
    const value = record[attr as keyof typeof record];
    return value && datetimeDistance(value);
  };
}

export function renderContactInfoType(translate: TranslateFunction) {
  return (record: { kind?: string }) => {
    if (record.kind) {
      return translate(`resources.attendees.fields.${record.kind.toLowerCase()}_contact_info`);
    }
  };
}

export function renderMoodleCourseCategory(category: { depth: number; name: string }) {
  const { depth, name } = category;

  if (depth > 1) {
    return `${"⎯⎯".repeat(depth - 1)} ${name}`;
  } else {
    return `${name}`;
  }
}

export function getBookingStatusColor(status: "VALID" | "WAITING" | "CANCELED" | "REBOOKED") {
  return {
    VALID: green[500],
    WAITING: orange[500],
    CANCELED: red[500],
    REBOOKED: red[500],
  }[status];
}

export function translatedResourceName(translate: TranslateFunction, opts: { smartCount?: number } = {}) {
  const { smartCount = 1 } = opts;

  return (
    resource: string | undefined,
    relationName: string | null | undefined,
    record: Record<string, unknown> | null = null,
    recordCount = 1,
  ) => {
    let key = `resources.${resource}.name`;

    if (
      resource === "events" &&
      relationName &&
      (relationName === "group_parents" || relationName === "group_children")
    ) {
      key = `resources.events.array_input_fields.${relationName}`;
    }

    if (resource === "emails" && record && record.invoice) {
      key = "resources.invoices.name";
    }

    if (
      resource === "emails" &&
      record &&
      record.scheduled_at &&
      new Date() < new Date(record.scheduled_at as string)
    ) {
      key = "emailing.scheduled_emails";
    }

    if (resource === "organization_users") {
      key = "resources.users.name";
    }

    if (recordCount > 1) {
      return `${recordCount} ${translate(key, { smart_count: recordCount })}`;
    } else {
      return translate(key, { smart_count: smartCount });
    }
  };
}

export function genusForResource(resource: string): "f" | "m" | "n" {
  const mapping: { [key: string]: "f" | "m" | "n" | undefined } = {
    audiences: "f",
    bookings: "f",
    categories: "f",
    event_bases: "n",
    emails: "f",
    contact_infos: "f",
    contexts: "f",
    event_dates: "n",
    events: "f",
    files: "n",
    group_children: "f",
    group_parents: "f",
    invoices: "f",
    lecturers: "f",
    partners: "m",
    prices: "m",
    rooms: "m",
    tags: "n",
  };

  return mapping[resource] || "n";
}

type EmailBearingContactInfo = Pick<ContactInfoType, "email">;

type ContactEmailRenderable = {
  main_contact_info?: EmailBearingContactInfo | null;
  invoice_contact_info?: EmailBearingContactInfo | null;
  invoice_dispatch_contact_info?: EmailBearingContactInfo | null;
  login_email?: string | null;
};

function renderFirstEmail(contactInfos: EmailBearingContactInfo[]) {
  const contactInfoEmails = contactInfos.filter((o) => !!o && o.email).map((o) => o.email);
  return contactInfoEmails[0] || "";
}

function removeNullContactInfos(contactInfos: (EmailBearingContactInfo | null | undefined)[]) {
  const finalContactInfos = [];

  for (const contactInfo of contactInfos) {
    if (contactInfo !== null && contactInfo !== undefined) {
      finalContactInfos.push(contactInfo);
    }
  }

  return finalContactInfos;
}

export function renderFirstContactOrLoginEmail(record?: ContactEmailRenderable) {
  if (!record) return "";
  const { invoice_dispatch_contact_info, invoice_contact_info, main_contact_info } = record;
  const contactInfos = [main_contact_info, invoice_contact_info, invoice_dispatch_contact_info];

  const contactEmail = renderFirstEmail(removeNullContactInfos(contactInfos));

  if (!contactEmail && record.login_email) {
    return record.login_email;
  }

  return contactEmail;
}

export function renderFirstContactEmail(record: UserRecordType | undefined) {
  if (!record) return "";
  const { invoice_dispatch_contact_info, invoice_contact_info, main_contact_info } = record;
  const contactInfos = [main_contact_info, invoice_contact_info, invoice_dispatch_contact_info];

  return renderFirstEmail(removeNullContactInfos(contactInfos));
}

export function renderFirstInvoiceEmail(record: PersonRecordType | undefined) {
  if (!record) return "";
  const { invoice_dispatch_contact_info, invoice_contact_info, main_contact_info } = record;
  const contactInfos = [invoice_dispatch_contact_info, invoice_contact_info, main_contact_info];

  return renderFirstEmail(removeNullContactInfos(contactInfos));
}

type RenderFullNameOptionsType = {
  skipTitle?: boolean;
  reverse?: boolean;
};

type FullNameRenderable = {
  id?: Identifier;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
} & ContactEmailRenderable;

export function renderFullName(record?: FullNameRenderable | null, options?: RenderFullNameOptionsType) {
  if (!record) return "";

  const { skipTitle = false, reverse = true } = options || {};
  const { first_name: firstName, last_name: lastName } = record;

  let fullName;
  if (firstName && lastName) {
    fullName = reverse ? `${lastName}, ${firstName}` : `${firstName} ${lastName}`;
  } else {
    fullName = lastName || firstName || "";
  }

  if (!fullName) return renderNamelessLabel(record);

  if (record.title && !skipTitle) {
    fullName += ` (${record.title})`;
  }

  return fullName;
}

export function renderInvoiceRecipientName(invoice: InvoiceRecordType): string {
  if (invoice.recipient_name) return invoice.recipient_name;

  const contactInfo = invoice.invoice_contact_info ?? invoice.main_contact_info;
  if (invoice.is_organization) return invoice.organization_name ?? contactInfo?.organization_name ?? "";

  return renderFullName(invoice);
}

/** Nameless records would render as an empty row or option, so fall back to whatever identifies them. */
function renderNamelessLabel(record: FullNameRenderable) {
  const email = renderFirstContactOrLoginEmail(record);
  if (email) return email;
  if (!record.id) return "";

  return i18nProvider.translate("no_name", { id: record.id });
}

export function renderCurrencyWithoutDecimals(amount: number): string {
  return flatRenderCurrency(amount, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

export function renderFullNameWithoutOptions(record?: PersonRecordType) {
  return renderFullName(record);
}

export function renderBooleanValue(value: unknown, translate: TranslateFunction) {
  if (typeof value != "boolean") {
    return;
  }

  if (value) {
    return translate("ra.boolean.true");
  }

  return translate("ra.boolean.false");
}

export function renderCategoryWithParent(category: CategoryRecordType) {
  if (category.parent_category_name) {
    return `${category.name} (${category.parent_category_name})`;
  }
  return category.name;
}

export function translatePermanentlyStoredAction(translate: TranslateFunction, resource: string, count = 1) {
  return translate("resources.document_templates.input.store_permanently", {
    scope: translate(`resources.${resource}.name`, { smart_count: count }),
  });
}

export function renderEventTimeSpan(record: EventRecordType) {
  return `${record.first_day ? dayGermanFormat(record.first_day) : ""}
    ${record.last_day && record.last_day != record.first_day ? ` - ${dayGermanFormat(record.last_day)}` : ""}`;
}

export function hasChildren(children?: ReactNode): boolean {
  if (!children) return false;

  const childrenArray = Children.toArray(children);

  return childrenArray.some((child) =>
    isValidElement<{ children?: ReactNode }>(child)
      ? !(child.type === Fragment && child.props.children === undefined)
      : true,
  );
}

export function renderTaxRate(taxRate: TaxRateRecordType) {
  return `${taxRate.amount} (${taxRate.untdid_category_display})`;
}

export function renderCustomFieldValue(
  value: unknown,
  options: {
    slug: string;
    dataType?: string;
    customField?: CustomFieldRecordType;
    customFieldMeta?: Record<string, { display: string | string[]; resource: string }>;
    translate: (key: string) => string;
  },
): string | null | undefined {
  const { slug, customField, dataType, customFieldMeta, translate } = options;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const finalDataType = dataType || customField?.data_type;

  // Check if there's meta for this field (pre-resolved relation)
  if (customFieldMeta?.[slug]) {
    const display = customFieldMeta[slug].display;
    return Array.isArray(display) ? display.join(", ") : display;
  }

  if (finalDataType === "BOOLEAN") {
    return renderBooleanValue(value, translate);
  }

  if (finalDataType === "DATE") {
    return dayGermanFormat(value as string);
  }

  if (finalDataType === "SELECT") {
    const selectValues = customField?.meta.select_values;

    if (Array.isArray(value)) {
      const selectValuesMap = new Map(selectValues?.map((sv) => [sv.value, sv.name]));
      return value.map((v) => selectValuesMap.get(v as string) || (v as string)).join(", ");
    }

    const matchingSelectValue = selectValues?.find((sv) => sv.value === value);
    return matchingSelectValue?.name || (value as string);
  }

  return value as string;
}

/** A position's gross, rounded to cents per position the way the backend sums it. */
export function positionGross(net: number, taxPercent: number | null | undefined) {
  return Math.round(net * (1 + (taxPercent ?? 0) / 100) * 100) / 100;
}
